import React, { useState } from "react";
import {
  useGetForgotPasswordUsersQuery,
  useResetUserPasswordMutation,
} from "../../slices/usersApiSlice";

import { toast } from "react-toastify";
import { FaKey } from "react-icons/fa";

const ForgotPasswordRequests = () => {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetForgotPasswordUsersQuery();

  const [
    resetUserPassword,
    { isLoading: isResetting },
  ] = useResetUserPasswordMutation();

  // Store the user currently being reset
  const [resettingUserId, setResettingUserId] = useState(null);

  const users = data?.users || [];

  // ============================================
  // Reset Password
  // ============================================

  const resetPasswordHandler = async (user) => {
    const confirmed = window.confirm(
      `Are you sure you want to reset the password for ${user.name}?\n\n` +
        `The password will be reset to the default password: 123456`,
    );

    if (!confirmed) {
      return;
    }

    try {
      setResettingUserId(user._id);

      await resetUserPassword(user._id).unwrap();

      toast.success(
        `${user.name}'s password has been reset successfully.`,
      );

      // The mutation invalidates the User tag,
      // so RTK Query will normally refetch automatically.
      // Keeping refetch here makes the behavior explicit.
      refetch();
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to reset password",
      );
    } finally {
      setResettingUserId(null);
    }
  };

  return (
    <div className="w-full p-4 sm:p-6">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Password Reset Requests
          </h2>

          <p className="text-sm text-gray-500 mt-1">
            Users from your coaching center who have requested a password reset.
          </p>
        </div>

        <button
          type="button"
          onClick={refetch}
          disabled={isLoading}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white
            hover:bg-indigo-700 transition
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* LOADING */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      )}

      {/* ERROR */}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="font-semibold text-red-700">
            Unable to load password reset requests.
          </p>

          <p className="text-sm text-red-600 mt-1">
            {error?.data?.message ||
              error?.error ||
              "Something went wrong."}
          </p>

          <button
            type="button"
            onClick={refetch}
            className="mt-3 px-4 py-2 rounded-lg bg-red-600 text-white
              hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      )}

      {/* EMPTY */}
      {!isLoading && !isError && users.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-8 text-center">
          <div className="text-4xl mb-3">✓</div>

          <h3 className="text-lg font-semibold text-gray-700">
            No Password Reset Requests
          </h3>

          <p className="text-sm text-gray-500 mt-1">
            There are currently no pending password reset requests.
          </p>
        </div>
      )}

      {/* REQUESTS */}
      {!isLoading && !isError && users.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

          {/* DESKTOP TABLE */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                    Name
                  </th>

                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                    Phone
                  </th>

                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                    Role
                  </th>

                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                    Status
                  </th>

                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {users.map((user) => {
                  const isCurrentUserResetting =
                    resettingUserId === user._id && isResetting;

                  return (
                    <tr
                      key={user._id}
                      className="hover:bg-gray-50 transition"
                    >
                      {/* NAME */}
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-800">
                          {user.name}
                        </div>

                        {user.email && (
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        )}
                      </td>

                      {/* PHONE */}
                      <td className="px-6 py-4 text-gray-700">
                        {user.phone}
                      </td>

                      {/* ROLE */}
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                            user.role === "student"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-purple-100 text-purple-700"
                          }`}
                        >
                          {user.role === "student"
                            ? "Student"
                            : "Center Admin"}
                        </span>
                      </td>

                      {/* STATUS */}
                      <td className="px-6 py-4">
                        <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                          Password Reset Requested
                        </span>
                      </td>

                      {/* ACTION */}
                      <td className="px-6 py-4">
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={() =>
                              resetPasswordHandler(user)
                            }
                            disabled={isResetting}
                            className="inline-flex items-center justify-center gap-2
                              px-4 py-2 rounded-lg
                              bg-orange-500 hover:bg-orange-600
                              text-white text-sm font-semibold
                              transition
                              disabled:opacity-50
                              disabled:cursor-not-allowed"
                          >
                            <FaKey />

                            {isCurrentUserResetting
                              ? "Resetting..."
                              : "Reset Password"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS */}
          <div className="md:hidden divide-y divide-gray-200">
            {users.map((user) => {
              const isCurrentUserResetting =
                resettingUserId === user._id && isResetting;

              return (
                <div
                  key={user._id}
                  className="p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {user.name}
                      </h3>

                      {user.email && (
                        <p className="text-sm text-gray-500 mt-1">
                          {user.email}
                        </p>
                      )}
                    </div>

                    <span
                      className={`shrink-0 px-2 py-1 rounded-full text-xs font-semibold ${
                        user.role === "student"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {user.role === "student"
                        ? "Student"
                        : "Center Admin"}
                    </span>
                  </div>

                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">
                        Phone
                      </span>

                      <span className="font-medium text-gray-700">
                        {user.phone}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">
                        Status
                      </span>

                      <span className="font-medium text-orange-600">
                        Reset Requested
                      </span>
                    </div>
                  </div>

                  {/* MOBILE ACTION */}
                  <button
                    type="button"
                    onClick={() =>
                      resetPasswordHandler(user)
                    }
                    disabled={isResetting}
                    className="w-full mt-5 inline-flex items-center justify-center gap-2
                      px-4 py-3 rounded-xl
                      bg-orange-500 hover:bg-orange-600
                      text-white font-semibold
                      transition
                      disabled:opacity-50
                      disabled:cursor-not-allowed"
                  >
                    <FaKey />

                    {isCurrentUserResetting
                      ? "Resetting Password..."
                      : "Reset Password"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* COUNT */}
      {!isLoading && !isError && users.length > 0 && (
        <div className="mt-4 text-sm text-gray-500">
          {users.length} password reset{" "}
          {users.length === 1
            ? "request"
            : "requests"}{" "}
          pending.
        </div>
      )}
    </div>
  );
};

export default ForgotPasswordRequests;